'use client';

import React, { useState, useMemo, JSX } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  TextField,
} from '@mui/material';
import { SPACING } from '@/stack-shared/constants/app.constants';

interface IAbstractTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  manualPagination?: boolean; // For server-side pagination
  fetchData?: (pagination: { pageIndex: number; pageSize: number }, search: string) => Promise<void>;
  totalRows?: number; // Total rows count for server-side pagination
  showSearch?: boolean;
}

const AbstractTable = <T,>({
  data,
  columns,
  manualPagination = false,
  fetchData,
  totalRows,
  showSearch = true
}: IAbstractTableProps<T>): JSX.Element => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');

  const filteredData = useMemo(() => {
    if (manualPagination || !search) return data;
    return data.filter((row) =>
      JSON.stringify(row).toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search, manualPagination]);

  const table = useReactTable({
    data: manualPagination ? data : filteredData,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    pageCount: manualPagination ? Math.ceil((totalRows || 0) / pagination.pageSize) : Math.ceil(filteredData.length / pagination.pageSize),
  });

  // Fetch data for server-side mode
  React.useEffect(() => {
    if (manualPagination && fetchData) {
      fetchData(pagination, search);
    }
  }, [pagination, search, fetchData, manualPagination]);


  const formatValue = (value: any, format?: string) => {
    if (value == null) return ''; // Handle null or undefined values
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      default:
        return value; // Return raw value if no format is specified
    }
  };

  // Calculate summary row for columns marked with `sum: true`
  const summaryRow = useMemo(() => {
    return columns.map((col: any) => {
      if (col.sum && col.accessorKey) {
        // Sum the column values, treating non-numeric values as 0
        const sum = data.reduce((acc, row: any) => acc + (Number(row[col.accessorKey]) || 0), 0);
        return formatValue(sum, col.format); // Apply formatting
      }
      return null; // No summary for non-sum columns
    });
  }, [columns, data]);


  return (
    <div style={{ backgroundColor: 'white', color: 'black', padding: SPACING.MD }}>
      {showSearch && <TextField
        label="Search"
        variant="outlined"
        size="small"
        fullWidth
        style={{ marginBottom: '1rem' }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />}
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ cursor: 'pointer' }}
                >
                  {header.isPlaceholder ? null : (
                    <>
                      {header.column.columnDef.header}{' '}
                      {header.column.getIsSorted() ? (header.column.getIsSorted() === 'asc' ? '🔼' : '🔽') : ''}
                    </>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
  {table.getRowModel().rows.map((row) => (
    <TableRow key={row.id}>
      {row.getVisibleCells().map((cell: any) => {
        const format = cell.column.columnDef.format; // Get the column's format
        return (
          <TableCell key={cell.id}>
            {formatValue(cell.getValue(), format)}
          </TableCell>
        );
      })}
    </TableRow>
  ))}
  {/* Render summary row */}
  {columns.some((col: any) => col.sum) && (
    <TableRow>
      {columns.map((col: any, index) => (
        <TableCell key={col.id || index}>
          {col.sum ? summaryRow[index] : ''}
        </TableCell>
      ))}
    </TableRow>
  )}
</TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              count={manualPagination ? totalRows || 0 : filteredData.length}
              rowsPerPage={pagination.pageSize}
              page={pagination.pageIndex}
              onPageChange={(_, newPage) => table.setPageIndex(newPage)}
              onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
              rowsPerPageOptions={[5, 10, 20]}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};

export default AbstractTable;
